import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { getCsrfToken } from '../../../config/csrf';

const EmployeeContext = createContext(null);

/* Backend wiring for the employee self-service portal, scoped to
   BrandonUserStoryAPI.md's EE-01 through EE-03 (README.md §4.4 for the
   /me/... endpoint table). Same db / refresh() / fetch-helper pattern as
   icib-admin/context/IcibAdminContext.js — everything here is resolved off
   the signed-in person's own session (GET/PATCH /me/...), no id/group
   parameter needed anywhere in this portal.

   Only EE-01 is wired up so far, under its own `// ==== EE-01` section
   below — GET /me/employees, the "what am I covered for" list. EE-02 (GET
   /me/employees/{id}) and EE-03 (PATCH /me) each get their own
   story-header section here when built, same convention as
   IcibAdminContext's ICIB-XX sections — touch only the section for the
   story you're working on. */

const EMPTY_DB = {
  me: null,
  // GET /me/employees (EE-01) — one Employee row per employer group the
  // person belongs to, each with its enrolments embedded. No form_data,
  // no product detail — that's EE-02's drill-down (GET /me/employees/{id}).
  employees: [],
  // Per-resource load failures, keyed the same as the fields above — a
  // failed resource keeps an empty array rather than blanking the whole
  // portal, same reasoning as IcibAdminContext's db.errors.
  errors: {},
};

// Same CSRF pattern as icib-admin/context/IcibAdminContext.js — Django's
// CsrfViewMiddleware rejects any unsafe-method request without a matching
// X-CSRFToken header. Not read from document.cookie — the SPA and API are
// on different registrable domains in every deployed environment, so this
// page's JS can never see the API's csrftoken cookie. getCsrfToken()
// (config/csrf.js) holds the token AuthContext fetched from GET /auth/csrf.
async function employeeFetch(path, { method = 'GET', body } = {}) {
  let res;
  try {
    const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    // Omit the header entirely when there's no token yet, rather than sending
    // '' — an empty X-CSRFToken header fails Django's length check ("incorrect
    // length") instead of the clearer "CSRF token missing" an absent header gets.
    const csrfToken = unsafe ? getCsrfToken() : null;
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    return { error: 'network_error', message: 'Could not reach the server. Check your connection and try again.' };
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) return data || { error: 'request_failed', message: `Request failed (${res.status}).` };
  return data;
}

// Defensive read for a list response, same reasoning as IcibAdminContext's
// `arr` helper — README doesn't show a worked example for every endpoint,
// so accept either a bare array or the documented `{ employees: [...] }` envelope.
function arr(data, ...keys) {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
}

export function EmployeeProvider({ children }) {
  const [db, setDb] = useState(EMPTY_DB);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [inFlightCount, setInFlightCount] = useState(0);
  // EE-02 cache — employee_id -> GET /me/employees/{id} result. Keyed
  // separately from `db.employees` (EE-01's list) rather than merged onto
  // it, same reasoning as IcibAdminContext's employeeDetails cache: not
  // every scheme's full detail is fetched up front, only the one(s) the
  // person actually opens.
  const [schemeDetails, setSchemeDetails] = useState({});
  // Stale-response guard, same pattern as IcibAdminContext.refresh —
  // a superseded in-flight call discards its result instead of applying it.
  const requestIdRef = useRef(0);

  // ==== EE-01 — GET /me/employees. Every group the signed-in person
  // belongs to, with enrolments embedded (§2.4 one Employee row per group,
  // §2.5 enrolments) — resolved from the authenticated User, no id/group
  // param needed. Fans out alongside GET /auth/me so `db.me`'s first_name
  // is available for the page greeting without a second round trip.
  const refresh = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    setInFlightCount((c) => c + 1);
    return Promise.all([
      employeeFetch('/auth/me'),
      employeeFetch('/me/employees'),
    ]).then(([me, employeesRes]) => {
      if (requestIdRef.current !== requestId) return null;
      const errors = {};
      if (employeesRes?.error) errors.employees = employeesRes.message || employeesRes.error;
      setDb({
        me: me && !me.error ? me : null,
        employees: arr(employeesRes, 'employees').map((e) => ({ ...e, id: e.employee_id || e.id })),
        errors,
      });
      setStatus('ready');
      return null;
    }).catch((err) => {
      if (requestIdRef.current !== requestId) return null;
      console.error('Employee dashboard load threw unexpectedly', err);
      setStatus('error');
      return { error: 'unexpected_error', message: 'Something went wrong loading your cover.' };
    }).finally(() => {
      setInFlightCount((c) => c - 1);
    });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const refreshing = inFlightCount > 0;

  // ==== EE-02 — GET /me/employees/{id}. The drill-down for one of the
  // signed-in person's own Employee records: `form_data` (view-only —
  // there's no employee-side PATCH for it, see BrandonUserStoryAPI.md
  // EE-02 / README §4.4) plus every enrolment resolved to its full
  // `product` (name + insurer_link), where EE-01's list only carries
  // `product_id`. Guarded server-side — 404s if `{id}` isn't one of the
  // caller's own records, wrong-owner and non-existent indistinguishable
  // by design (same as icib-admin's group-scoping 404s). Cached per
  // employee_id, same on-demand pattern as IcibAdminContext's
  // loadEmployeeDetail — call again (e.g. after a refresh) to force a
  // refetch; on error nothing is cached, so the caller sees its own
  // returned `{ error, message }` rather than a stale/blank entry.
  async function loadSchemeDetail(employeeId) {
    const res = await employeeFetch(`/me/employees/${employeeId}`);
    if (!res?.error) {
      const normalized = { ...res, enrolments: (res.enrolments || []).map((en) => ({ ...en, id: en.enrolment_id || en.id })) };
      setSchemeDetails((m) => ({ ...m, [employeeId]: normalized }));
      return normalized;
    }
    return res;
  }

  // ==== EE-03 — PATCH /me. Corrects the signed-in person's own identity
  // fields (first_name, last_name, dob, phone, address — §2.1) — the same
  // five fields ICIB-02b's Update writes, just self-service and scoped to
  // the caller rather than an admin acting on someone else's record. No
  // `form_data` here — that's still icib_admin-only via ICIB-02d; this
  // form has nothing to submit for it. On success, patches `db.me` in
  // place with the response body rather than a full refresh(), same
  // "merge the mutation's own response" reasoning as IcibAdminContext's
  // mutation helpers.
  async function updateMe(fields) {
    const res = await employeeFetch('/me', { method: 'PATCH', body: fields });
    if (!res?.error) setDb((d) => ({ ...d, me: { ...d.me, ...res } }));
    return res;
  }

  const value = { db, status, refresh, refreshing, schemeDetails, loadSchemeDetail, updateMe };

  return <EmployeeContext.Provider value={value}>{children}</EmployeeContext.Provider>;
}

export function useEmployee() {
  const ctx = useContext(EmployeeContext);
  if (!ctx) throw new Error('useEmployee must be used within an EmployeeProvider');
  return ctx;
}
